import { useState, useEffect, useRef } from 'react';

export default function WhyChooseGI2() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const isPausedRef = useRef(false);
  const pauseTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const scrollPositionRef = useRef(0);
  const lastTouchRef = useRef(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const features = [
    {
      icon: 'ri-shield-check-line',
      title: 'Quality First',
      description: 'Products sourced from trusted global suppliers',
      color: 'teal',
    },
    {
      icon: 'ri-price-tag-3-line',
      title: 'Competitive Pricing',
      description: 'Fair wholesale rates with volume discounts',
      color: 'orange',
    },
    {
      icon: 'ri-truck-line',
      title: 'Fast Delivery',
      description: 'Pan-India logistics with timely dispatch',
      color: 'teal',
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Dedicated Support',
      description: 'Responsive service for B2B clients',
      color: 'orange',
    },
    {
      icon: 'ri-box-3-line',
      title: 'Wide Range',
      description: 'Consumables, lamination, and printer parts',
      color: 'teal',
    },
    {
      icon: 'ri-hand-coin-line',
      title: 'Flexible Terms',
      description: 'B2B payment options available',
      color: 'orange',
    },
  ];

  // Infinite auto-scroll for mobile
  useEffect(() => {
    if (!isVisible || window.innerWidth >= 768) return;

    const container = scrollContainerRef.current;
    if (!container) return;

    // Speed: pixels per frame (60fps) - adjust for smoothness
    // Target: one card (~320px) in ~3 seconds = ~1.78px per frame
    const scrollSpeed = 1.8;

    const animate = () => {
      if (!isPausedRef.current && container) {
        scrollPositionRef.current += scrollSpeed;
        
        // Get the width of one set of cards (half of total since we duplicated)
        const singleSetWidth = container.scrollWidth / 2;
        
        // Reset position when we've scrolled past one full set
        if (scrollPositionRef.current >= singleSetWidth) {
          scrollPositionRef.current = 0;
        }
        
        container.scrollLeft = scrollPositionRef.current;
      }
      
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (pauseTimeoutRef.current) {
        clearTimeout(pauseTimeoutRef.current);
      }
    };
  }, [isVisible]);

  // Handle manual touch interaction
  const handleTouchStart = () => {
    lastTouchRef.current = Date.now();
    isPausedRef.current = true;
    
    if (pauseTimeoutRef.current) {
      clearTimeout(pauseTimeoutRef.current);
    }
  };

  const handleTouchEnd = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // Sync scroll position after manual interaction
    scrollPositionRef.current = container.scrollLeft;
    
    // Resume auto-scroll after 6 seconds
    pauseTimeoutRef.current = setTimeout(() => {
      isPausedRef.current = false;
    }, 6000);
  };

  return (
    <section ref={sectionRef} className="py-12 md:py-16 lg:py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4 md:px-6 lg:px-12">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-8 md:mb-12 lg:mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Why Choose GI2?
          </h2>
          <p className="text-base md:text-lg text-gray-600">
            Your trusted partner for printer consumables and lamination supplies
          </p>
        </div>

        {/* Desktop/Tablet Grid */}
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              className={`group bg-white rounded-2xl p-6 md:p-8 border border-gray-200 hover:border-teal-600 hover:shadow-xl transition-all duration-500 cursor-pointer ${
                isVisible ? 'animate-fade-in-up' : 'opacity-0'
              }`}
              style={{
                animationDelay: `${index * 100}ms`,
                animationFillMode: 'forwards',
              }}
            >
              {/* Icon */}
              <div
                className={`w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-xl mb-5 transition-all duration-500 group-hover:scale-110 group-hover:rotate-3 ${
                  feature.color === 'teal'
                    ? 'bg-teal-100 text-teal-700 group-hover:bg-teal-700 group-hover:text-white'
                    : 'bg-orange-100 text-orange-600 group-hover:bg-orange-600 group-hover:text-white'
                }`}
              >
                <i className={`${feature.icon} text-2xl md:text-3xl`}></i>
              </div>

              {/* Content */}
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 group-hover:text-teal-700 transition-colors duration-300">
                {feature.title}
              </h3>
              <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        {/* Mobile Infinite Scroll Carousel */}
        <div className="md:hidden overflow-x-hidden -mx-4">
          <div 
            ref={scrollContainerRef}
            className="flex gap-3 overflow-x-auto scrollbar-hide px-4"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            style={{ 
              scrollBehavior: 'auto',
              WebkitOverflowScrolling: 'touch'
            }}
          >
            {/* Original set of cards */}
            {features.map((feature, index) => (
              <div
                key={`original-${index}`}
                className={`group bg-white rounded-xl p-4 border border-gray-200 active:border-teal-600 active:shadow-lg transition-all duration-300 cursor-pointer flex-shrink-0 ${
                  isVisible ? 'animate-fade-in-up' : 'opacity-0'
                }`}
                style={{
                  width: 'calc(100vw - 2.5rem)',
                  maxWidth: '320px',
                  animationDelay: `${index * 100}ms`,
                  animationFillMode: 'forwards',
                }}
              >
                {/* Icon */}
                <div
                  className={`w-11 h-11 flex items-center justify-center rounded-lg mb-3 transition-all duration-300 active:scale-105 ${
                    feature.color === 'teal'
                      ? 'bg-teal-100 text-teal-700'
                      : 'bg-orange-100 text-orange-600'
                  }`}
                >
                  <i className={`${feature.icon} text-xl`}></i>
                </div>

                {/* Content */}
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-gray-600 leading-snug line-clamp-1">
                  {feature.description}
                </p>
              </div>
            ))}
            
            {/* Duplicated set for seamless loop */}
            {features.map((feature, index) => (
              <div
                key={`duplicate-${index}`}
                className="group bg-white rounded-xl p-4 border border-gray-200 active:border-teal-600 active:shadow-lg transition-all duration-300 cursor-pointer flex-shrink-0"
                style={{
                  width: 'calc(100vw - 2.5rem)',
                  maxWidth: '320px',
                }}
              >
                {/* Icon */}
                <div
                  className={`w-11 h-11 flex items-center justify-center rounded-lg mb-3 transition-all duration-300 active:scale-105 ${
                    feature.color === 'teal'
                      ? 'bg-teal-100 text-teal-700'
                      : 'bg-orange-100 text-orange-600'
                  }`}
                >
                  <i className={`${feature.icon} text-xl`}></i>
                </div>

                {/* Content */}
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-gray-600 leading-snug line-clamp-1">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.6s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }

        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }

        .line-clamp-1 {
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      `}</style>
    </section>
  );
}
